import React from "react";
import useDirect from "../../../hook/useDirect";
import { repeatedCohortCardCount, sectionIds } from "../const/course";
import type { CohortItem, CourseFact, CoursePageMap, HeroMediaItem, RequirementsContent, SharingItem } from "../type/course";
import {
  getFactValue,
  getHeroImage,
  getSection,
  getSharingEmbedUrl,
  getSharingPreviewImage,
  scrollToSection,
} from "../util/course";

export type CourseDetailsPageConfig = Readonly<{
  courseName: string;
  pageMap: CoursePageMap;
}>;

export function useCourseDetailsPageData({ courseName, pageMap }: CourseDetailsPageConfig) {
  const { directTo } = useDirect();
  const [playingVideoId, setPlayingVideoId] = React.useState<string | null>(null);
  const [heroMediaIndex, setHeroMediaIndex] = React.useState(0);

  const descriptionContent = getSection<{
    paragraphs: string[];
    featuredSharing?: { label: string; itemRef: string };
  }>(pageMap, sectionIds.description);
  const cohortContent = getSection<{ cohorts: CohortItem[] }>(pageMap, sectionIds.cohorts);
  const sharingContent = getSection<{ items: SharingItem[] }>(pageMap, sectionIds.sharing);
  const factsContent = getSection<{ facts: CourseFact[]; requirements: RequirementsContent }>(pageMap, sectionIds.facts);

  const heroImage = getHeroImage(cohortContent.cohorts);
  const isEnded = pageMap.status.state === "ended";
  const hasSharingItems = sharingContent.items.length > 0;
  const featuredSharing = hasSharingItems ? sharingContent.items[0] : null;

  const heroPrimaryAction = isEnded
    ? { label: "瀏覽課程資料", onClick: () => scrollToSection("course-facts") }
    : { label: pageMap.header.primaryCta.label, onClick: () => directTo(pageMap.header.primaryCta.action.target) };

  const footerAction = isEnded
    ? { label: "查看過往屆別", onClick: () => scrollToSection("cohorts") }
    : { label: pageMap.header.primaryCta.label, onClick: () => directTo(pageMap.header.primaryCta.action.target) };

  const togglePlayingVideo = (id: string) => setPlayingVideoId((current) => (current === id ? null : id));
  const heroMediaItems: HeroMediaItem[] = [
    ...cohortContent.cohorts
      .filter((cohort) => Boolean(cohort.coverImage))
      .map((cohort) => ({
        id: `cohort-image-${cohort.id}`,
        kind: "image" as const,
        title: cohort.title.includes("學員紀錄") ? cohort.title : `${cohort.title} 學員紀錄`,
        thumbnailImage: cohort.coverImage ?? heroImage,
        actionLabel: "查看過往屆別",
        onAction: () => scrollToSection("cohorts"),
      })),
  ];
  if (featuredSharing) {
    heroMediaItems.push({
      id: `featured-video-${featuredSharing.id}`,
      kind: "video",
      title: featuredSharing.title,
      thumbnailImage: getSharingPreviewImage(featuredSharing, heroImage),
      embedUrl: getSharingEmbedUrl(featuredSharing),
      actionLabel: "播放影片",
    });
  }

  const boundedHeroMediaIndex =
    heroMediaItems.length === 0
      ? 0
      : ((heroMediaIndex % heroMediaItems.length) + heroMediaItems.length) % heroMediaItems.length;

  return {
    courseName,
    pageMap,
    descriptionContent,
    sharingItems: sharingContent.items,
    factsContent,
    featuredSharing,
    heroImage,
    keyFacts: factsContent.facts.filter((item) => ["hours", "price", "language"].includes(item.key)),
    classTime: getFactValue(factsContent.facts, "classTime"),
    heroSummary: isEnded
      ? "課程已結束，你仍可查看課程安排、入學要求、學員分享與過往屆別紀錄。"
      : "查看課程安排、修讀資訊、學員分享與過往屆別紀錄。",
    heroPrimaryAction,
    footerAction,
    isEnded,
    playingVideoId,
    togglePlayingVideo,
    heroMediaItems,
    activeHeroMedia: heroMediaItems[boundedHeroMediaIndex],
    goPrevHeroMedia: () => {
      setHeroMediaIndex((current) => current - 1);
      setPlayingVideoId(null);
    },
    goNextHeroMedia: () => {
      setHeroMediaIndex((current) => current + 1);
      setPlayingVideoId(null);
    },
    cohortCarouselItems:
      cohortContent.cohorts.length === 0
        ? Array.from({ length: repeatedCohortCardCount }).map((_, index) => ({
            id: `cohort-carousel-${index}`,
            image: heroImage,
            title: "第一屆",
            description: "精選相片與學員作品紀錄，快速回顧這一屆的學習重點與成果。",
          }))
        : cohortContent.cohorts.map((cohort, index) => ({
            id: `cohort-carousel-${cohort.id}`,
            image: cohort.coverImage ?? heroImage,
            title: cohort.title.includes("學員紀錄") ? cohort.title : `Cohort ${index + 1} 學員紀錄`,
            description: cohort.description,
          })),
  };
}

export type CourseDetailsPageData = ReturnType<typeof useCourseDetailsPageData>;

